<template>
  <div class="my-8">
    <h1 class="text-xl font-bold mb-4">Account Transactions</h1>

    <!-- Account Selection & Date Filters -->
    <div class="bg-white border rounded-lg p-4 mb-6">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <!-- Account Selection -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Select Account
          </label>
          <select
            v-model="selectedAccountId"
            class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            @change="loadTransactions"
          >
            <option value="">-- Select Account --</option>
            <option
              v-for="account in data.accounts"
              :key="account.id"
              :value="account.id"
            >
              {{ account.name || `Account #${account.id}` }} - GMD {{ formatPrice(account.balance) }}
            </option>
          </select>
        </div>

        <!-- Date From -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Date From
          </label>
          <input
            type="date"
            v-model="dateFrom"
            class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <!-- Date To -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Date To
          </label>
          <input
            type="date"
            v-model="dateTo"
            class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <!-- Search Button -->
        <div class="flex items-end">
          <button
            @click="loadTransactions"
            :disabled="!selectedAccountId || isLoading"
            class="w-full bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <svg
              v-if="isLoading"
              class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {{ isLoading ? 'Loading...' : 'Search' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Empty State - No Account Selected -->
    <div
      v-if="!selectedAccountId && !isLoading"
      class="bg-white border rounded-lg p-8 text-center text-gray-500"
    >
      <svg
        class="mx-auto h-12 w-12 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
        />
      </svg>
      <p class="mt-4">Please select an account to view transactions</p>
    </div>

    <!-- Summary Section -->
    <div v-if="summary" class="mb-6">
      <!-- Period Info -->
      <div class="bg-gray-50 border rounded-lg p-4 mb-4">
        <div class="flex justify-between items-center">
          <div class="text-sm text-gray-600">
            Period: {{ summary.date_from | moment('DD/MM/YYYY') }} - {{ summary.date_to | moment('DD/MM/YYYY') }}
          </div>
          <div class="flex items-center">
            <span
              v-if="summary.balance_matches"
              class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
            >
              <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
              </svg>
              Verified
            </span>
            <span
              v-else
              class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800"
            >
              <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
              </svg>
              Discrepancy - Contact Support
            </span>
          </div>
        </div>
      </div>

      <!-- Starting Balance -->
      <div class="bg-white border rounded-lg p-4 mb-4">
        <div class="text-sm text-gray-500">Starting Balance</div>
        <div class="text-2xl font-bold text-gray-800">
          GMD {{ formatPrice(summary.starting_balance) }}
        </div>
      </div>

      <!-- Breakdown Cards -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <!-- Deposits -->
        <div class="bg-white border rounded-lg p-4 border-l-4 border-l-green-500">
          <div class="text-sm font-medium text-gray-700">Deposits</div>
          <div class="text-xl font-bold text-green-600 mt-1">
            +GMD {{ formatPrice(summary.breakdown?.deposits?.total_credit || 0) }}
          </div>
          <div class="text-xs text-gray-500 mt-1">
            {{ summary.breakdown?.deposits?.count || 0 }} transactions
          </div>
        </div>

        <!-- Sales -->
        <div class="bg-white border rounded-lg p-4 border-l-4 border-l-blue-500">
          <div class="text-sm font-medium text-gray-700">Sales</div>
          <div class="text-xl font-bold text-red-600 mt-1">
            -GMD {{ formatPrice(summary.breakdown?.sales?.total_debit || 0) }}
          </div>
          <div class="text-xs text-gray-500 mt-1">
            {{ summary.breakdown?.sales?.count || 0 }} transactions
            <span v-if="summary.breakdown?.sales?.total_commission" class="ml-2 text-green-600">
              (Commission: GMD {{ formatPrice(summary.breakdown.sales.total_commission) }})
            </span>
          </div>
        </div>

        <!-- Reversals -->
        <div class="bg-white border rounded-lg p-4 border-l-4 border-l-orange-500">
          <div class="text-sm font-medium text-gray-700">Reversals</div>
          <div class="text-xl font-bold text-red-600 mt-1">
            -GMD {{ formatPrice(summary.breakdown?.reversals?.total_debit || 0) }}
          </div>
          <div class="text-xs text-gray-500 mt-1">
            {{ summary.breakdown?.reversals?.count || 0 }} transactions
          </div>
        </div>
      </div>

      <!-- Net Change & Ending Balance -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div class="bg-white border rounded-lg p-4">
          <div class="text-sm text-gray-500">Net Change</div>
          <div class="text-xl font-bold" :class="summary.net_change >= 0 ? 'text-green-600' : 'text-red-600'">
            GMD {{ formatPrice(summary.net_change) }}
          </div>
        </div>
        <div class="bg-white border rounded-lg p-4" :class="summary.balance_matches ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'">
          <div class="flex justify-between items-start">
            <div>
              <div class="text-sm text-gray-500">Ending Balance</div>
              <div class="text-xl font-bold text-gray-800">
                GMD {{ formatPrice(summary.ending_balance) }}
              </div>
            </div>
            <div v-if="summary.balance_matches" class="text-green-600">
              <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
            </div>
            <div v-else class="text-red-600">
              <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
              </svg>
            </div>
          </div>
          <div v-if="!summary.balance_matches" class="text-xs text-red-600 mt-2">
            Expected: GMD {{ formatPrice(summary.expected_ending_balance) }}
          </div>
        </div>
      </div>

      <!-- Totals Summary -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div class="bg-white border rounded-lg p-4">
          <div class="text-sm text-gray-500">Total Credits</div>
          <div class="text-xl font-bold text-green-600">
            GMD {{ formatPrice(summary.total_credits) }}
          </div>
        </div>
        <div class="bg-white border rounded-lg p-4">
          <div class="text-sm text-gray-500">Total Debits</div>
          <div class="text-xl font-bold text-red-600">
            GMD {{ formatPrice(summary.total_debits) }}
          </div>
        </div>
        <div class="bg-white border rounded-lg p-4">
          <div class="text-sm text-gray-500">Total Commission</div>
          <div class="text-xl font-bold text-green-600">
            GMD {{ formatPrice(summary.total_commission) }}
          </div>
        </div>
        <div class="bg-white border rounded-lg p-4">
          <div class="text-sm text-gray-500">Total Transactions</div>
          <div class="text-xl font-bold text-gray-800">
            {{ summary.total_transactions }}
          </div>
        </div>
      </div>
    </div>

    <!-- Transactions Table -->
    <div class="flex flex-col" v-if="selectedAccountId">
      <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <!-- Table Header -->
          <div class="flex justify-between items-center mb-2" v-if="summary">
            <h2 class="text-lg font-semibold text-gray-800">
              Transactions ({{ summary.total_transactions }} total)
            </h2>
            <div v-if="pagination" class="text-sm text-gray-600">
              Page {{ pagination.current_page }} of {{ pagination.total_pages }}
            </div>
          </div>
          <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <!-- No Results -->
            <div
              v-if="transactions.length === 0 && !isLoading"
              class="bg-white p-8 text-center text-gray-500"
            >
              <svg
                class="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
              </svg>
              <p class="mt-4">No transactions found for the selected period</p>
            </div>

            <!-- Table -->
            <table v-else class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Date
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Type
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Credit
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Debit
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Balance
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Commission
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Audit
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr
                  v-for="(transaction, index) in transactions"
                  :key="index"
                  class="hover:bg-gray-50"
                  :class="{ 'bg-red-50': hasDiscrepancy(index) }"
                >
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm text-gray-900">
                      {{ transaction.created_at | moment('DD MMM YYYY') }}
                    </div>
                    <div class="text-xs text-gray-500">
                      {{ transaction.created_at | moment('HH:mm:ss') }}
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full"
                      :class="getTypeClass(transaction.type)"
                    >
                      {{ transaction.type | capitalize }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-right">
                    <div class="text-sm font-medium text-green-600">
                      {{ transaction.credit ? `GMD ${formatPrice(transaction.credit)}` : '-' }}
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-right">
                    <div class="text-sm font-medium text-red-600">
                      {{ transaction.debit ? `GMD ${formatPrice(transaction.debit)}` : '-' }}
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-right">
                    <div class="text-sm font-bold text-gray-900">
                      GMD {{ formatPrice(transaction.balance) }}
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-right">
                    <div class="text-sm text-gray-600">
                      {{ transaction.commission ? `GMD ${formatPrice(transaction.commission)}` : '-' }}
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-center">
                    <div v-if="hasDiscrepancy(index)" class="flex flex-col items-center">
                      <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                        <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                        </svg>
                        Discrepancy
                      </span>
                      <span class="text-xs text-red-600 mt-1">
                        Expected: {{ formatPrice(getExpectedBalance(index)) }}
                      </span>
                    </div>
                    <div v-else>
                      <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                        <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                        </svg>
                        OK
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Load More / Infinite Scroll -->
            <div 
              v-if="pagination"
              ref="loadMoreTrigger" 
              class="bg-white px-4 py-3 border-t border-gray-200"
            >
              <div class="text-center">
                <p class="text-sm text-gray-500 mb-2">
                  Showing {{ transactions.length }} of {{ pagination.total_items }} transactions
                </p>
                <div v-if="isLoadingMore" class="flex justify-center items-center py-2">
                  <svg class="animate-spin h-5 w-5 text-blue-600 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span class="text-sm text-gray-600">Loading more...</span>
                </div>
                <button
                  v-else-if="pagination.has_more"
                  @click="loadMore"
                  class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Load More
                </button>
                <p v-else class="text-sm text-gray-400">
                  All transactions loaded
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import FormatPrice from "@/mixins/FormatPrice";

export default {
  props: ["data"],

  mixins: [FormatPrice],

  data: () => ({
    transactions: [],
    summary: null,
    pagination: null,
    currentPage: 1,
    perPage: 50,
    isLoading: false,
    isLoadingMore: false,
    selectedAccountId: "",
    dateFrom: "",
    dateTo: "",
  }),

  filters: {
    capitalize(value) {
      if (!value) return "";
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
  },

  created() {
    // Set default date range to current month
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    this.dateFrom = this.formatDate(firstDay);
    this.dateTo = this.formatDate(lastDay);
  },

  methods: {
    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    getTypeClass(type) {
      const classes = {
        sales: "bg-blue-100 text-blue-800",
        deposit: "bg-green-100 text-green-800",
        revart: "bg-orange-100 text-orange-800",
      };
      return classes[type] || "bg-gray-100 text-gray-800";
    },

    /**
     * Get the expected balance for a transaction based on the previous transaction's balance
     * Transactions are in DESC order (newest first), so "previous" is the next index
     */
    getExpectedBalance(index) {
      const transaction = this.transactions[index];
      const credit = parseFloat(transaction.credit) || 0;
      const debit = parseFloat(transaction.debit) || 0;

      // Get the previous balance (next row since desc order, or starting_balance for last item)
      let previousBalance;
      if (index < this.transactions.length - 1) {
        // There's a next row (older transaction)
        previousBalance = parseFloat(this.transactions[index + 1].balance) || 0;
      } else {
        // This is the oldest transaction in the current list
        // Use starting_balance from summary if available
        previousBalance = this.summary?.starting_balance || 0;
      }

      // Expected balance = previous balance + credit - debit
      return previousBalance + credit - debit;
    },

    /**
     * Check if a transaction has a balance discrepancy
     */
    hasDiscrepancy(index) {
      const transaction = this.transactions[index];
      const actualBalance = parseFloat(transaction.balance) || 0;
      const expectedBalance = this.getExpectedBalance(index);

      // Allow for small floating point differences (0.01 tolerance)
      return Math.abs(actualBalance - expectedBalance) > 0.01;
    },

    async loadTransactions(page, append = false) {
      // Handle cases where page is not a valid number (e.g., called from @change event)
      if (typeof page !== 'number' || isNaN(page)) {
        page = 1;
        append = false;
      }

      if (!this.selectedAccountId) {
        return;
      }

      if (append) {
        this.isLoadingMore = true;
      } else {
        this.isLoading = true;
        this.transactions = [];
        this.summary = null;
        this.pagination = null;
      }

      this.currentPage = page;

      try {
        const agentId = this.$route.params.id;

        const { data } = await axios.get(
          `/agent/${agentId}/accounts/${this.selectedAccountId}/transactions`,
          {
            params: {
              date_from: this.dateFrom || undefined,
              date_to: this.dateTo || undefined,
              page: parseInt(page, 10),
              per_page: parseInt(this.perPage, 10),
            },
          }
        );

        if (append) {
          // Append new transactions to existing list
          this.transactions = [...this.transactions, ...(data.transactions || [])];
        } else {
          this.transactions = data.transactions || [];
        }
        
        // Only update summary on first page or if not yet set
        if (page === 1 || !this.summary) {
          this.summary = {
            agent_id: data.agent_id,
            account_id: data.account_id,
            date_from: data.date_from,
            date_to: data.date_to,
            starting_balance: data.starting_balance || 0,
            ending_balance: data.ending_balance || 0,
            expected_ending_balance: data.expected_ending_balance || 0,
            total_credits: data.total_credits || 0,
            total_debits: data.total_debits || 0,
            total_commission: data.total_commission || 0,
            net_change: data.net_change || 0,
            total_transactions: data.total_transactions || 0,
            balance_matches: data.balance_matches !== false,
            breakdown: data.breakdown || {},
          };
        }

        // Update pagination
        this.pagination = data.pagination || null;
        
        this.isLoading = false;
        this.isLoadingMore = false;
      } catch (e) {
        console.error("Error loading account transactions:", e);
        this.isLoading = false;
        this.isLoadingMore = false;
        this.$toast?.error?.("Failed to load transactions") ||
          alert("Failed to load transactions");
      }
    },

    loadMore() {
      if (this.pagination && this.pagination.has_more && !this.isLoadingMore) {
        this.loadTransactions(this.currentPage + 1, true);
      }
    },

    handleScroll() {
      const trigger = this.$refs.loadMoreTrigger;
      if (!trigger || this.isLoadingMore || !this.pagination?.has_more) return;

      const rect = trigger.getBoundingClientRect();
      const isVisible = rect.top <= window.innerHeight + 100;

      if (isVisible) {
        this.loadMore();
      }
    },
  },

  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
};
</script>
